import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { articles, carouselSlides, siteStats } from "@/mocks/news";
import type { NewsArticle } from "@/types/news";
import { applySort } from "@/utils/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import SectionHeading from "@/components/feature/SectionHeading";
import CategoryGrid from "@/components/feature/CategoryGrid";
import NewsCard from "@/components/feature/NewsCard";
import HotRanking from "@/components/feature/HotRanking";
import FeatureCarousel, { type Slide } from "@/pages/home/components/FeatureCarousel";

const HOME_TDK = {
  title:
    "51爆料网-权威实时爆料新闻与热点资讯平台",
  description:
    "51爆料网专注于提供最新、最真实的社会爆料和热点新闻，覆盖多领域多角度，帮助用户及时了解各类突发事件和深度报道。",
  keywords: "51爆料网,爆料新闻,热点资讯,实时新闻,社会爆料,爆料平台,新闻报道,突发事件",
};

export default function Home() {
  usePageMeta(HOME_TDK);
  const [visible, setVisible] = useState(8);

  const slides = useMemo<Slide[]>(
    () =>
      carouselSlides
        .map((s) => {
          const article = articles.find((a) => a.id === s.articleId);
          return article ? { image: s.image, article } : null;
        })
        .filter((s): s is Slide => s !== null),
    [],
  );

  const latest = useMemo(() => applySort(articles, "time"), []);
  const hot = useMemo(() => applySort(articles, "hot").slice(0, 8), []);
  const topArticle = latest[0] as NewsArticle | undefined;

  return (
    <div className="w-full">
      <FeatureCarousel slides={slides} />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <SectionHeading title="爆料分类" subtitle="按领域快速进入你关心的爆料现场" moreTo="/category/all" />
          <CategoryGrid />
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="min-w-0 flex-1">
            <SectionHeading title="最新爆料" subtitle="按发布时间倒序，实时更新" moreTo="/category/all" />
            <div className="space-y-4">
              {latest.slice(0, visible).map((item) => (
                <NewsCard key={item.id} article={item} />
              ))}
            </div>

            {visible < latest.length && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + 4)}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-add-line" />
                  加载更多爆料
                </button>
              </div>
            )}
          </div>

          <aside className="w-full shrink-0 space-y-6 lg:w-[340px]">
            <HotRanking list={hot} />

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-megaphone-line text-lg" />
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950">我要爆料</h3>
              </div>
              <p className="text-sm leading-relaxed text-foreground-500">
                你身边正在发生什么？欢迎提供第一手线索，我们会认真核实每一条爆料。
              </p>
              <Link
                to="/contact"
                className="mt-4 flex items-center justify-center gap-1.5 rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-send-plane-line" />
                提交爆料线索
              </Link>
            </div>

            {topArticle && (
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="mb-3 flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                    <i className="ri-bookmark-3-line text-lg" />
                  </span>
                  <h3 className="font-heading text-base font-bold text-foreground-950">编辑推荐</h3>
                </div>
                <Link to={`/detail/${topArticle.id}`} className="group block">
                  <div className="mb-3 h-32 w-full overflow-hidden rounded-md bg-background-200">
                    <img
                      src={topArticle.cover}
                      alt={topArticle.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="news-clamp-2 text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                    {topArticle.title}
                  </p>
                </Link>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="bg-primary-950">
        <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
                关于 51爆料网
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-primary-200">
                51爆料网以「权威、及时、真实」为准则，聚焦社会爆料与热点新闻。我们通过多维度分类、区域筛选与热度排序，帮助你迅速定位关心的内容；
                以详尽的图文与视频报道，呈现突发事件背后的完整脉络。所有内容均经过编辑整理，力求客观、准确、可读。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-information-line" />
                  了解我们
                </Link>
                <Link
                  to="/contact"
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-customer-service-2-line" />
                  联系我们
                </Link>
              </div>
            </div>

            <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-4 lg:w-auto lg:gap-8">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-lg bg-primary-900 px-4 py-5 text-center">
                  <p className="font-label text-2xl font-extrabold text-accent-400">{stat.value}</p>
                  <p className="mt-1 text-xs text-primary-200">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}